import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import "../styles/ServiceFormAccessiblePickers.css";

type Props = {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** YYYY-MM-DD inclusive; días anteriores no se pueden elegir (p. ej. hoy en alta de servicio). */
  minDateYmd?: string;
};

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function toYMD(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseYMD(s: string): Date | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d)
    return null;
  return dt;
}

function getMonthCells(
  viewYear: number,
  viewMonth: number
): { day: number; inMonth: boolean; date: Date }[] {
  const first = new Date(viewYear, viewMonth, 1);
  const lastDay = new Date(viewYear, viewMonth + 1, 0).getDate();
  const startPad = (first.getDay() + 6) % 7;
  const items: { day: number; inMonth: boolean; date: Date }[] = [];
  const prevLast = new Date(viewYear, viewMonth, 0).getDate();
  for (let i = 0; i < startPad; i++) {
    const day = prevLast - startPad + i + 1;
    items.push({
      day,
      inMonth: false,
      date: new Date(viewYear, viewMonth - 1, day),
    });
  }
  for (let d = 1; d <= lastDay; d++) {
    items.push({
      day: d,
      inMonth: true,
      date: new Date(viewYear, viewMonth, d),
    });
  }
  const end = (7 - (items.length % 7)) % 7;
  for (let i = 1; i <= end; i++) {
    items.push({
      day: i,
      inMonth: false,
      date: new Date(viewYear, viewMonth + 1, i),
    });
  }
  return items;
}

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const AccessibleDatePicker: React.FC<Props> = ({
  id,
  name,
  label,
  value,
  onChange,
  minDateYmd,
}) => {
  const dialogTitleId = useId();
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const prevMonthRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 400 });

  const selected = parseYMD(value);
  const initialView = selected ?? new Date();
  const [viewYear, setViewYear] = useState(initialView.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialView.getMonth());

  const syncViewToValue = useCallback(() => {
    const d = parseYMD(value);
    if (d) {
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  }, [value]);

  useEffect(() => {
    if (open) syncViewToValue();
  }, [open, syncViewToValue]);

  const fireChange = (v: string) => {
    onChange({
      target: { name, value: v },
      currentTarget: { name, value: v },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const monthTitle = new Date(viewYear, viewMonth, 1).toLocaleDateString(
    "es-ES",
    { month: "long", year: "numeric" }
  );

  const displayText = selected
    ? selected.toLocaleDateString("es-ES", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  const cells = getMonthCells(viewYear, viewMonth);
  const today = new Date();
  const todayYmd = toYMD(today);
  const minBoundary = minDateYmd ? parseYMD(minDateYmd) : null;
  const prevMonthDisabled = Boolean(
    minBoundary &&
      (viewYear < minBoundary.getFullYear() ||
        (viewYear === minBoundary.getFullYear() &&
          viewMonth <= minBoundary.getMonth()))
  );

  const prevMonth = () => {
    const d = new Date(viewYear, viewMonth - 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const nextMonth = () => {
    const d = new Date(viewYear, viewMonth + 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const close = () => setOpen(false);

  useLayoutEffect(() => {
    if (!open || !toggleRef.current) return;
    const margin = 8;
    /** Alto aproximado del panel (nav + días + pie); evita que quede cortado abajo */
    const POPOVER_ESTIMATE = 430;

    const update = () => {
      const el = toggleRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const maxW = Math.min(400, vw - margin * 2);
      const w = maxW;
      const centerX = rect.left + rect.width / 2;
      let left = centerX - w / 2;
      left = Math.max(margin, Math.min(left, vw - w - margin));

      let top = rect.bottom + margin;
      if (top + POPOVER_ESTIMATE > vh - margin) {
        top = rect.top - POPOVER_ESTIMATE - margin;
      }
      if (top < margin) {
        top = margin;
      }

      setCoords({ top, left, width: w });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => prevMonthRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pickDay = (date: Date) => {
    const ymd = toYMD(date);
    if (minDateYmd && ymd < minDateYmd) return;
    fireChange(ymd);
    close();
    toggleRef.current?.focus();
  };

  const pickToday = () => {
    const n = new Date();
    const ymd = toYMD(n);
    if (minDateYmd && ymd < minDateYmd) return;
    fireChange(ymd);
    setViewYear(n.getFullYear());
    setViewMonth(n.getMonth());
    close();
    toggleRef.current?.focus();
  };

  const clearDate = () => {
    fireChange("");
    close();
    toggleRef.current?.focus();
  };

  const popover = (
    <>
      <div
        className="a11y-date__backdrop"
        aria-hidden
        onClick={close}
      />
      <div
        id={`${id}-dialog`}
        className="a11y-date__popover"
        role="dialog"
        aria-modal="true"
        aria-labelledby={dialogTitleId}
        style={{
          top: coords.top,
          left: coords.left,
          width: coords.width,
        }}
      >
        <div className="a11y-date__nav">
          <button
            ref={prevMonthRef}
            type="button"
            className="a11y-date__nav-btn"
            onClick={prevMonth}
            disabled={prevMonthDisabled}
            aria-label="Mes anterior"
          >
            <ChevronLeft size={28} strokeWidth={2.5} aria-hidden />
          </button>
          <h2 id={dialogTitleId} className="a11y-date__month-title">
            {monthTitle}
          </h2>
          <button
            type="button"
            className="a11y-date__nav-btn"
            onClick={nextMonth}
            aria-label="Mes siguiente"
          >
            <ChevronRight size={28} strokeWidth={2.5} aria-hidden />
          </button>
        </div>

        <div className="a11y-date__weekdays" aria-hidden>
          {WEEKDAYS.map((w) => (
            <div key={w} className="a11y-date__weekday">
              {w}
            </div>
          ))}
        </div>

        <div
          className="a11y-date__grid"
          role="group"
          aria-label="Días del mes"
        >
          {cells.map((cell, idx) => {
            const ymd = toYMD(cell.date);
            const isSel = value === ymd;
            const isTo = todayYmd === ymd;
            const beforeMin = Boolean(minDateYmd && ymd < minDateYmd);
            const labelLong = cell.date.toLocaleDateString("es-ES", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            });
            return (
              <button
                key={`${ymd}-${idx}`}
                type="button"
                disabled={beforeMin}
                className={[
                  "a11y-date__day",
                  !cell.inMonth ? "a11y-date__day--outside" : "",
                  isTo ? "a11y-date__day--today" : "",
                  isSel ? "a11y-date__day--selected" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => pickDay(cell.date)}
                aria-label={
                  beforeMin ? `${labelLong}, no disponible` : labelLong
                }
                aria-pressed={isSel}
                aria-current={isTo ? "date" : undefined}
              >
                {cell.day}
              </button>
            );
          })}
        </div>

        <div className="a11y-date__footer">
          <button
            type="button"
            className="a11y-date__action a11y-date__action--ghost"
            onClick={clearDate}
          >
            Borrar
          </button>
          <button
            type="button"
            className="a11y-date__action a11y-date__action--primary"
            onClick={pickToday}
            disabled={Boolean(minDateYmd && todayYmd < minDateYmd)}
          >
            Hoy
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="a11y-date">
      <label className="a11y-date__label" htmlFor={`${id}-toggle`}>
        {label}
      </label>
      <div className="a11y-date__shell">
        <button
          ref={toggleRef}
          id={`${id}-toggle`}
          type="button"
          className={`a11y-date__value ${
            !displayText ? "a11y-date__value--placeholder" : ""
          }`}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-controls={open ? `${id}-dialog` : undefined}
          onClick={() => setOpen((o) => !o)}
        >
          {displayText || "Elegir fecha (día, mes y año)"}
        </button>
        <button
          type="button"
          className="a11y-date__icon-btn"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Cerrar calendario" : "Abrir calendario"}
          aria-expanded={open}
          aria-controls={open ? `${id}-dialog` : undefined}
        >
          <Calendar size={28} strokeWidth={2} aria-hidden />
        </button>
      </div>
      {open && typeof document !== "undefined"
        ? createPortal(popover, document.body)
        : null}
    </div>
  );
};

export default AccessibleDatePicker;
